import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTheme } from "@mui/material/styles";
import { Breadcrumbs, styled } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { isValidName, validateEmail } from "../../utils";
import { addUserRequest } from "../../redux/slices/userSlice";
import { useDispatch, useSelector } from "react-redux";

const AddAdminUser = ({ navigateHome }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    userRole: "",
    preferredLanguage: "en",
    notifications: [],
  });

  const { loading } = useSelector((state) => state.userManagement);
  const [formError, setFormError] = useState({
    firstName: false,
    lastName: false,
    email: false,
    userRole: false,
  });

  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.between("sm", "stm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("stm", "mtl"));

  const CustomBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
    "& li.MuiBreadcrumbs-separator": {
      margin: 0,
    },
  }));

  // Handle text field and dropdown changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setFormError((prev) => ({ ...prev, [name]: false }));
  };

  const checkBoxName = {
    releaseNotes: "NT001",
    productionOutages: "NT002",
    uatOutages: "NT003",
    newsletters: "PT001",
    surveys: "PT002",
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    const mappedName = checkBoxName[name];
    setFormData((prev) => ({
      ...prev,
      notifications: checked
        ? [...prev.notifications, mappedName]
        : prev.notifications.filter((notif) => notif !== mappedName),
    }));
  };

  const isNotif = (name) => {
    const mappedName = checkBoxName[name];
    return formData.notifications.includes(mappedName);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const firstNameError = isValidName(formData.firstName);
    const lastNameError = isValidName(formData.lastName);
    let emailError = "";

    const radioRoleMapping = {
      "Super Admin": "AUR001",
      "Business Admin": "AUR002",
      "Business Users": "AUR003",
      "IT Admin": "AUR004",
      "Support Admin": "AUR005",
      "Support User": "AUR006",
    };

    if (!formData.email) {
      emailError = "*Mandatory";
    } else if (validateEmail(formData.email)) {
      emailError = "*Please enter valid email";
    }

    const error = {
      firstName: firstNameError.valid ? "" : firstNameError.error,
      lastName: lastNameError.valid ? "" : lastNameError.error,
      email: emailError,
      userRole: !formData.userRole ? "*Mandatory" : "",
    };
    setFormError(error);

    if (Object.values(error).some((error) => error)) {
      return;
    }
    const formattedData = {
      admin_user_first_name: formData.firstName,
      admin_user_middle_name: formData.middleName,
      admin_user_last_name: formData.lastName,
      admin_user_email: formData.email,
      admin_user_role_id: radioRoleMapping[formData.userRole],
      preferred_language: formData.preferredLanguage,
      user_opted_notifications: formData.notifications,
    };
    dispatch(addUserRequest({ ...formattedData, navigate: handleBack }));
  };

  const handleBack = () => {
    navigate("/");
    navigateHome(3, 3);
  };

  const radioRole = [
    "Super Admin",
    "Business Admin",
    "Business Users",
    "IT Admin",
    "Support Admin",
    "Support User",
  ];

  // Handle role change
  const handleRoleChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, userRole: value }));
    setFormError((prev) => ({ ...prev, userRole: false }));
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  return (
    <Grid container spacing={2} padding={2}>
      <Grid
        container
        sx={{
          mt: "6.5rem",
          ml: isMobile ? "28px" : isTablet ? "32px" : "86px",
        }}
      >
        <CustomBreadcrumbs
          separator={
            <NavigateNextIcon fontSize="small" sx={{ color: "#00507861" }} />
          }
        >
          <Typography
            sx={{
              fontFamily: "Open Sans",
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: "18px",
              cursor: "pointer",
              color: "#000000",
            }}
            component="div"
            onClick={handleBack}
          >
            Manage User
          </Typography>
          <Typography
            sx={{
              fontFamily: "Open Sans",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "14px",
              color: "#000000",
            }}
          >
            Add User
          </Typography>
        </CustomBreadcrumbs>
      </Grid>

      <Grid
        container
        spacing={2}
        alignItems="center"
        mb={2}
        sx={{
          mt: "20px",
          mb: "24px",
          mx: isMobile ? "12px" : isTablet ? "14px" : "68px",
          mr: isMobile || isTablet ? 0 : "78px",
        }}
      >
        <Grid item xs={12} stm={9} md={9} lg={10.3}>
          <Typography
            variant="h6"
            sx={{
              mb: "14px",
              fontFamily: "Open Sans",
              fontWeight: 700,
              fontSize: "36px",
              lineHeight: "48px",
            }}
          >
            Add User
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: "white",
          boxShadow: 10,
          zIndex: 1,
          borderRadius: "10px",
          mx: isMobile ? "22px" : isTablet ? "32px" : "78px",
          mr: isMobile || isTablet ? 0 : "78px",
        }}
      >
        <Grid
          item
          xs={12}
          sx={{ backgroundColor: "#EFFAFF", height: "24px" }}
        ></Grid>

        {/* User Details Section */}
        <Grid item xs={12} md={12} lg={12} padding={3}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Open Sans",
              fontWeight: "700",
              fontSize: "16px",
              lineHeight: "20px",
              color: "#005078",
            }}
          >
            User Details
          </Typography>
          <Grid container spacing={2} sx={{ my: "12px" }}>
            <Grid item xs={12} stm={6} md={6} lg={4}>
              <TextField
                name="firstName"
                required
                label="First Name"
                variant="standard"
                error={formError.firstName}
                helperText={formError.firstName}
                value={formData.firstName}
                onChange={handleChange}
                fullWidth
                sx={{
                  mb: "16px",
                }}
              />
            </Grid>
            <Grid item xs={12} stm={6} md={6} lg={4}>
              <TextField
                variant="standard"
                name="middleName"
                label="Middle Name"
                value={formData.middleName}
                onChange={handleChange}
                fullWidth
                sx={{
                  mb: "16px",
                }}
              />
            </Grid>
            <Grid item xs={12} stm={6} md={6} lg={4}>
              <TextField
                variant="standard"
                required
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                error={formError.lastName}
                helperText={formError.lastName}
                onChange={handleChange}
                fullWidth
                sx={{
                  mb: "16px",
                }}
              />
            </Grid>
            <Grid item xs={12} stm={6} md={6} lg={4}>
              <TextField
                variant="standard"
                required
                name="email"
                label="Email Address"
                error={formError.email}
                helperText={formError.email}
                value={formData.email}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>

        {/* User Role Section with Radio Buttons */}
        <Grid item xs={12} md={12} lg={12} padding={3}>
          <Divider
            sx={{
              backgroundColor: "#E2E3E5",
              ml: "-23px",
              mr: "-23px",
            }}
          />
          <Grid container spacing={2} sx={{ mt: "32px" }}>
            <Grid
              item
              xs={12}
              stm={6}
              md={6}
              lg={4}
              sx={{ mt: "22px", mb: "32px" }}
            >
              <Typography
                variant="h6"
                sx={{
                  mt: "-40px",
                  fontFamily: "Open Sans",
                  fontWeight: "700",
                  fontSize: "16px",
                  lineHeight: "20px",
                  color: "#005078",
                }}
              >
                User Type
              </Typography>
              <FormControl fullWidth sx={{ mt: "50px" }} required>
                <Select
                  variant="standard"
                  size="small"
                  displayEmpty
                  name="userRole"
                  error={formError.userRole}
                  helperText={formError.userRole && "This field is Mandatory"}
                  value={formData.userRole}
                  onChange={handleRoleChange}
                  renderValue={(selected) =>
                    selected || (
                      <Typography
                        sx={{ color: formError.userRole ? "red" : "#717173" }}
                      >
                        Select Role*
                      </Typography>
                    )
                  }
                  IconComponent={() => (open ? <ExpandLess /> : <ExpandMore />)}
                  onOpen={handleToggle}
                  onClose={handleToggle}
                >
                  {radioRole.map((role, index) => [
                    <MenuItem key={role} value={role}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        sx={{ mb: "-10px", mt: "-10px" }}
                      >
                        {role}
                        <Radio
                          checked={formData.userRole === role}
                          value={role}
                          onClick={(e) => {
                            // handleChange(e)
                            e.stopPropagation();
                            setFormData((prev) => ({
                              ...prev,
                              userRole: role,
                            }));
                          }}
                        />
                      </Box>
                    </MenuItem>,
                    index < radioRole.length - 1 && (
                      <Divider sx={{ mb: "-10px" }} />
                    ),
                  ])}
                </Select>
                {formError.userRole && (
                  <FormHelperText sx={{ color: "red", ml: "0px" }}>
                    *Mandatory
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Language Section */}
            <Grid item xs={12} md={6} stm={6} lg={4} sx={{ mt: "-20px" }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: "700",
                  fontSize: "16px",
                  lineHeight: "20px",
                  color: "#005078",
                }}
              >
                Language
              </Typography>
              <FormControl fullWidth>
                <FormLabel
                  sx={{
                    color: "#4A4F55",
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "18px",
                    pt: "15px",
                  }}
                >
                  Preferred Language for Emails
                </FormLabel>
                <Select
                  variant="standard"
                  name="preferredLanguage"
                  value={formData.preferredLanguage}
                  onChange={handleChange}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        {/* Notification List */}
        <Grid item xs={12} md={12} lg={12} padding={3}>
          <Divider
            sx={{
              backgroundColor: "#E2E3E5",
              ml: "-23px",
              mr: "-23px",
            }}
          />
          <Grid container spacing={2} sx={{ mt: "32px" }}>
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: "700",
                  fontSize: "16px",
                  lineHeight: "20px",
                  color: "#005078",
                }}
              >
                Notification List
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ color: "#1460AA" }}
                      name="releaseNotes"
                      checked={isNotif("releaseNotes")}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Release Notes"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans",
                      fontWeight: "400",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#000000",
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ color: "#1460AA" }}
                      name="productionOutages"
                      checked={isNotif("productionOutages")}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Production Environment MI, Outages and Planned Maintenance"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans",
                      fontWeight: "400",
                      fontSize: "14px",
                      lineHeight: "20px",
                      // mt: { stm: "-5px", md:"16px", lg: "0px"  },
                      color: "#000000",
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ color: "#1460AA" }}
                      name="uatOutages"
                      checked={isNotif("uatOutages")}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="UAT (Gold) Environment Outages and Planned Maintenance"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans",
                      fontWeight: "400",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#000000",
                    },
                  }}
                />
              </FormGroup>
            </Grid>

            <Grid item xs={12} md={6} stm={6} lg={4}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ color: "#1460AA" }}
                      name="newsletters"
                      checked={isNotif("newsletters")}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Newsletters"
                  sx={{
                    mt: { sm: "-10px", stm: "16px" },
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans",
                      fontWeight: "400",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#000000",
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ color: "#1460AA" }}
                      name="surveys"
                      checked={isNotif("surveys")}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Surveys"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans",
                      fontWeight: "400",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#000000",
                    },
                  }}
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12} padding={3}>
          <Divider
            sx={{
              backgroundColor: "#E2E3E5",
              ml: "-23px",
              mr: "-23px",
              mb: "32px",
            }}
          />
          <Button
            variant="contained"
            type="submit"
            color="primary"
            sx={{
              textTransform: "none",
              borderRadius: "24px",
              minWidth: "137px",
              height: "44px",
            }}
            fullWidth={isMobile}
            onClick={handleSubmit}
          >
            {loading ? (
              <CircularProgress
                size={20}
                sx={{ color: "white" }}
              ></CircularProgress>
            ) : (
              "Add User"
            )}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AddAdminUser;
